import { NAME_MAX, parseJoinCrew, sanitizeName, type CrewChoice, type GameMode } from "@tiny-seas/shared";
import { fetchSeaStatus } from "../net.ts";
import { lobbyRoyaleText } from "../royale-status.ts";
import { savedLookCode } from "../scene/character-look.ts";
import { settings } from "../settings.ts";
import { Changelog, currentVersion } from "./changelog.ts";
import { byId, h, setText, toggle } from "./dom.ts";

export interface LobbyHandlers {
  join(name: string, crew: CrewChoice | undefined, mode: GameMode): Promise<void>;
}

const TEMPLATE = `
<main class="lobby" data-testid="lobby">
  <div class="card lobby-card">
    <h1>Tiny Seas</h1>
    <p class="tagline">One shared sea for up to 100 sailors — humans and AI.</p>
    <p>Choose a crew, hunt treasure and defend your gold. Sail to the X, bring treasure home,
       or raid a rival ship. Stock cannonballs and repair planks — leaks sink ships!</p>
    <form data-ref="form" novalidate>
      <label for="name">Your pirate name</label>
      <input id="name" data-ref="name" maxlength="${NAME_MAX}" autocomplete="nickname" required placeholder="e.g. Salty Sam" />
      <fieldset class="lobby-mode"><legend>Mode</legend><label><input type="radio" name="mode" value="sea" data-ref="modeSea" /> Open seas</label><label><input type="radio" name="mode" value="royale" data-ref="modeRoyale" /> Battle royale</label></fieldset>
      <p class="hint" data-ref="modeHint" hidden>No islands. Grab floating loot, hold the treasure for 5 minutes or be the last ship afloat.</p>
      <p class="hint" data-ref="royaleStatus" hidden></p>
      <div class="lobby-actions">
        <button type="submit" data-ref="join" class="primary">Set sail</button>
        <button type="button" data-ref="customize">Customize your sailor</button>
      </div>
      <p class="hint" data-ref="look"></p>
      <p class="hint" data-ref="population" hidden></p>
      <p class="hint" data-ref="full" hidden>The sea is full. Try again in a moment.</p>
      <p class="error" data-ref="error" role="alert" hidden></p>
      <button type="button" data-ref="retry" hidden>Retry</button>
      <p class="hint" data-ref="busy" hidden>Connecting…</p>
    </form>
    <p class="hint">Desktop browser with a mouse and keyboard required. Sail solo or bring your crew.</p>
    <div class="row">
      <span class="hint">Version ${currentVersion}</span>
      <button type="button" class="small" data-ref="changelog">What’s new</button>
    </div>
  </div>
</main>`;

export class Lobby {
  readonly root: HTMLElement;
  private lastAction: (() => Promise<void>) | null = null;
  private disposed = false;
  private statusPending = false;
  private readonly statusTimer: number;
  private readonly changelog: Changelog;

  constructor(parent: HTMLElement, handlers: LobbyHandlers, initialError?: string) {
    this.root = h("div");
    this.root.innerHTML = TEMPLATE;
    parent.append(this.root);
    this.changelog = new Changelog(this.root);
    byId(this.root, "changelog", HTMLButtonElement).addEventListener("click", () => this.changelog.open());
    byId(this.root, "customize", HTMLButtonElement).addEventListener("click", () => location.assign("/characters.html"));
    setText(byId(this.root, "look", HTMLParagraphElement), savedLookCode() ? "You sail in the look you made in the Character creator." : "You sail as the classic crewmate.");
    const name = byId(this.root, "name", HTMLInputElement);
    name.value = settings.value.name;
    const params = new URLSearchParams(location.search);
    const rawCrew = params.get("crew");
    const inviteCrew = parseJoinCrew({ crew: rawCrew === "new" ? "new" : rawCrew !== null && /^\d{1,2}$/.test(rawCrew) ? Number(rawCrew) : undefined });
    let mode: GameMode = params.get("mode") === "royale" ? "royale" : "sea";
    const modeSea = byId(this.root, "modeSea", HTMLInputElement);
    const modeRoyale = byId(this.root, "modeRoyale", HTMLInputElement);
    const modeHint = byId(this.root, "modeHint", HTMLParagraphElement);
    const royaleStatus = byId(this.root, "royaleStatus", HTMLParagraphElement);
    (mode === "royale" ? modeRoyale : modeSea).checked = true;
    toggle(modeHint, mode === "royale");
    (name.value ? byId(this.root, "join", HTMLButtonElement) : name).focus();
    const pollStatus = async (): Promise<void> => {
      if (this.statusPending || this.disposed) return;
      this.statusPending = true;
      try {
        const status = await fetchSeaStatus(mode);
        if (this.disposed) return;
        const population = byId(this.root, "population", HTMLParagraphElement);
        if (status) setText(population, `${status.population} / ${status.capacity} sailors · ${status.humans} humans · ${status.crewBots} crew bots · ${status.npcSailors} enemy sailors · ${status.reserved} reconnecting`);
        toggle(population, status !== null);
        toggle(byId(this.root, "full", HTMLParagraphElement), status !== null && status.humans + status.reserved >= status.capacity);
        if (status?.royale) setText(royaleStatus, lobbyRoyaleText(status.royale));
        toggle(royaleStatus, mode === "royale" && !!status?.royale);
      } finally {
        this.statusPending = false;
      }
    };
    this.statusTimer = window.setInterval(() => void pollStatus(), 5000);
    void pollStatus();
    const onMode = (): void => {
      mode = modeRoyale.checked ? "royale" : "sea";
      const url = new URLSearchParams(location.search);
      if (mode === "royale") url.set("mode", "royale");
      else url.delete("mode");
      history.replaceState(null, "", `?${url}`);
      toggle(modeHint, mode === "royale");
      toggle(royaleStatus, false);
      void pollStatus();
    };
    modeSea.addEventListener("change", onMode);
    modeRoyale.addEventListener("change", onMode);

    const readName = (): string | null => {
      const n = sanitizeName(name.value);
      if (!n) {
        this.error("Please enter a name (1–16 characters).", false);
        name.focus();
        return null;
      }
      settings.update({ name: n });
      return n;
    };
    const run = async (action: () => Promise<void>): Promise<void> => {
      this.lastAction = action;
      this.setBusy(true);
      try {
        await action();
      } finally {
        this.setBusy(false);
      }
    };
    byId(this.root, "form", HTMLFormElement).addEventListener("submit", (e) => {
      e.preventDefault();
      const n = readName();
      if (!n) return;
      void run(() => handlers.join(n, inviteCrew, mode));
    });
    byId(this.root, "retry", HTMLButtonElement).addEventListener("click", () => {
      if (this.lastAction) void run(this.lastAction);
    });
    if (initialError) this.error(initialError, false);
  }

  error(message: string, retryable: boolean): void {
    const e = byId(this.root, "error", HTMLParagraphElement);
    setText(e, message);
    toggle(e, true);
    toggle(byId(this.root, "retry", HTMLButtonElement), retryable);
  }

  private setBusy(busy: boolean): void {
    toggle(byId(this.root, "busy", HTMLParagraphElement), busy);
    if (busy) toggle(byId(this.root, "error", HTMLParagraphElement), false);
    for (const id of ["join", "retry"]) byId(this.root, id, HTMLButtonElement).disabled = busy;
  }

  dispose(): void {
    this.disposed = true;
    clearInterval(this.statusTimer);
    this.changelog.dispose();
    this.root.remove();
  }
}

export function fatalScreen(parent: HTMLElement, title: string, body: string): void {
  parent.replaceChildren(h("main", { class: "lobby" }, [h("div", { class: "card lobby-card" }, [h("h1", {}, [title]), h("p", {}, [body])])]));
}
